import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import RestaurantHomeCard from "./RestaurantHomeCard";
import Image from "components/AppImage";
import Icon from "components/AppIcon";
import AppSpinner from "components/ui/AppSpinner";
import { base_url } from "Backend/api";
import toast from "react-hot-toast";
import NoRestaurantScreen from "pages/NoRestaurantScreen";
import FavoriteToggleDialog, {
  useFavoriteToggleDialog,
} from "components/favorites/FavoriteToggleDialog";
import {
  emitFavoriteToggled,
  subscribeFavoriteToggled,
} from "utils/favoriteEvents";

const RestaurantsSection = () => {
  const navigate = useNavigate();

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState({});

  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("restaurants_section_view") || "grid"
  );
  const { showDialog, dialogMessage, showFavoriteDialog } =
    useFavoriteToggleDialog();

  const resolveImage = (restaurant) => {
    const rawUrl = restaurant?.image_url;
    if (rawUrl && /^https?:\/\//i.test(String(rawUrl))) return String(rawUrl);
    if (rawUrl && String(rawUrl).startsWith("/")) {
      return `${String(base_url).replace(/\/$/, "")}${rawUrl}`;
    }
    if (restaurant?.image) {
      const p = String(restaurant.image).replace(/^\//, "");
      return `${base_url}/storage/app/public/${p}`;
    }
    return "/assets/images/logo.jpeg";
  };


  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${base_url}/api/restaurants`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined
          }
        });
        const result = await response.json();

        if (result.status) {
          setRestaurants(result.data);

          const favState = {};
          result.data.forEach((r) => {
            favState[r.slug] = r.is_favorite || false;
          });
          setFavorites(favState);
        }
      } catch (error) {
        console.error("Error fetching restaurants:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();
  }, []);

  useEffect(() => {
    localStorage.setItem("restaurants_section_view", viewMode);
  }, [viewMode]);

  useEffect(() => {
    return subscribeFavoriteToggled(({ storeType, slug, isFavorite }) => {
      if (storeType !== "restaurant" || !slug) return;

      setFavorites((prev) => ({
        ...prev,
        [slug]: !!isFavorite,
      }));

      setRestaurants((prev) =>
        prev.map((restaurant) =>
          restaurant?.slug === slug
            ? { ...restaurant, is_favorite: !!isFavorite }
            : restaurant
        )
      );
    });
  }, []);


  const handleRestaurantClick = (slug) => {
    navigate(`/restaurant/${slug}`);
  };

  // Toggle favorite
  const handleToggleFavorite = async (slug) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${base_url}/api/favorites/restaurants/${slug}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (result.status) {
        const newFavState =
          typeof result?.data?.is_favorite === "boolean"
            ? result.data.is_favorite
            : !favorites[slug];

        setFavorites((prev) => ({
          ...prev,
          [slug]: newFavState,
        }));

        setRestaurants((prev) =>
          prev.map((restaurant) =>
            restaurant?.slug === slug
              ? { ...restaurant, is_favorite: newFavState }
              : restaurant
          )
        );

        emitFavoriteToggled({
          storeType: "restaurant",
          slug,
          isFavorite: newFavState,
        });
        showFavoriteDialog(newFavState);
      } else {
        toast.error(result.message || "حدث خطأ");
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("حدث خطأ في الشبكة");
    }
  };

  if (!loading && restaurants.length === 0) {
    return (

      <NoRestaurantScreen compact />
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <AppSpinner />
      </div>
    );
  }

  return (
    <>
    <section
      id="exclusive"
      className="mb-18 w-full scroll-mt-32 px-4 py-2 md:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-7xl w-full">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-[14px] md:text-[16px] lg:text-[18px] font-bold text-foreground font-[cairo]">
            كل المطاعم
          </h2>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`flex h-8 w-8 items-center justify-center rounded-[8px]  transition ${
                viewMode === "grid"
                  ? " bg-[#FDEDE9] text-[#F93209]"
                  : "border-[#E7E7E7]  text-[#8A8A8A]"
              }`}
              aria-label="عرض شبكي"
            >
              <Icon name="Grid2x2" size={15} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`flex h-8 w-8 items-center justify-center rounded-[8px]  transition ${
                viewMode === "list"
                  ? "bg-[#FDEDE9]  text-[#F93209]"
                  : "border-[#E7E7E7]  text-[#8A8A8A]"
              }`}
              aria-label="عرض قائمة"
            >
              <Icon name="ListFilter" size={15} />
            </button>
          </div>
        </div>

        {viewMode === "grid" ? (
        <div className="space-y-3 md:space-y-3.5">
          {restaurants.map((restaurant) => (
            <RestaurantHomeCard
              key={restaurant.id}
              restaurant={restaurant}
              isFavorite={!!favorites[restaurant.slug]}
              onCardClick={handleRestaurantClick}
              onFavoriteClick={handleToggleFavorite}
            />
          ))}
        </div>
        ) : (
          <div className="space-y-3 md:space-y-3.5">
            {restaurants.map((item) => (
              <div
                dir="ltr"
                onClick={() => handleRestaurantClick(item.slug || item.id)}
                key={item.id}
                className="bg-white rounded-[10px] border border-[#ECECEC] overflow-hidden cursor-pointer p-2.5 shadow-sm"
              >
                <div className="flex gap-1.5 font-[cairo]">
                  <div className="w-[78px] shrink-0 text-[12px]">
                    <div className="inline-flex items-center gap-1 rounded-full bg-[#00B200] px-2 py-[1px] font-[cairo] text-white">
                      <span>{item?.rating?.average ?? item?.average_rating ?? "0.0"}</span>
                      <span>({item?.rating?.reviews_count ?? item?.reviews_count ?? 0})</span>
                      <Icon name="Star" size={11} color="#FFCF33" fill="#FFCF33" />
                    </div>
                    <div className=" text-[#00A651] flex flex-col items-start gap-0" dir="ltr">
                      <span dir="rtl" className="text-[#6B7280]">
                        {item?.delivery?.estimate_minutes ?? "—"} دقيقة
                      </span>
                      <div dir="rtl" className="flex items-center gap-1">
                        <img src="/assets/images/bbagg.svg" className="h-3 w-3" alt="" />
                        <span>{item?.delivered_orders_count ?? 0} طلب</span>
                      </div>
                      <div dir="rtl"  className=" flex items-center justify-start gap-1 text-[11px] text-[#00B200]">
                      <span className="h-2 w-2 rounded-full bg-[#00B200]" />
                      {item?.status_label || "نشط"}
                    </div>
                    </div>
                  </div>

                  <div dir="rtl" className="min-w-0 flex-1">
                    <h3 className="text-[14px] font-semibold text-[#1F2937] line-clamp-1 font-[cairo]">
                      {item?.name}
                    </h3>
                    <div className="text-[11px] text-[#4B5563] mt-1 line-clamp-1">
                      أقل سعر للطلب {item?.minimum_order ?? "—"} ل.س
                    </div>
                    <div className="mt-1.5 flex items-center gap-1 text-[10px] text-[#6B7280] line-clamp-1">
                      <Icon name="MapPin" size={13} color="#F93209" />
                      {item?.full_address || item?.location?.address || "—"}
                    </div>
                    
                  </div>

                  <div className="relative w-[82px] shrink-0">
                    <div className="h-[82px] w-[82px] overflow-hidden rounded-[8px]">
                      <Image src={resolveImage(item)} alt={item?.name} className="w-full h-full object-cover" />
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleFavorite(item.slug);
                      }}
                      className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center"
                    >
                      <Icon
                        name="Heart"
                        size={13}
                        color={item?.is_favorite || favorites[item.slug] ? "#F93209" : "#ccc"}
                        fill={item?.is_favorite || favorites[item.slug] ? "#F93209" : "none"}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
    <FavoriteToggleDialog open={showDialog} message={dialogMessage} />
</>
  );
};

export default RestaurantsSection;